import { Button, DatePicker, Dropdown, Switch } from 'antd';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  RiCalendarLine,
  RiHeartPulseLine,
  RiArrowDownSLine,
} from 'react-icons/ri';
import { Divider } from '@/shared/ui/Divider';
import type { DateRange, StudyType } from '../types/patientDetail';
import { DATE_RANGE_LABELS, STUDY_TYPE_LABELS } from '../types/patientDetail';
import styles from './PatientDetailSubHeader.module.css';

interface PatientDetailSubHeaderProps {
  studyCount: number;
  dateRange: DateRange;
  onDateRangeChange: (value: DateRange) => void;
  studyType: StudyType;
  onStudyTypeChange: (value: StudyType) => void;
  showReportHistory: boolean;
  onShowReportHistoryChange: (value: boolean) => void;
  customDateRange: [string, string] | null;
  onCustomDateRangeChange: (value: [string, string] | null) => void;
  onResetFilters: () => void;
}

const dateRangeItems: MenuProps['items'] = (
  Object.keys(DATE_RANGE_LABELS) as DateRange[]
).map((key) => ({ key, label: DATE_RANGE_LABELS[key] }));

const studyTypeItems: MenuProps['items'] = (
  Object.keys(STUDY_TYPE_LABELS) as StudyType[]
).map((key) => ({ key, label: STUDY_TYPE_LABELS[key] }));

export default function PatientDetailSubHeader({
  studyCount,
  dateRange,
  onDateRangeChange,
  studyType,
  onStudyTypeChange,
  showReportHistory,
  onShowReportHistoryChange,
  customDateRange,
  onCustomDateRangeChange,
  onResetFilters,
}: PatientDetailSubHeaderProps) {
  const hasActiveFilters = dateRange !== 'all' || studyType !== 'all';
  const handleDateRangeClick: MenuProps['onClick'] = ({ key }) => {
    onDateRangeChange(key as DateRange);
  };

  const handleStudyTypeClick: MenuProps['onClick'] = ({ key }) => {
    onStudyTypeChange(key as StudyType);
  };

  const handleCustomDateChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      onCustomDateRangeChange([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD'),
      ]);
    } else {
      onCustomDateRangeChange(null);
    }
  };

  const rangePickerValue: [Dayjs, Dayjs] | null = customDateRange
    ? [dayjs(customDateRange[0]), dayjs(customDateRange[1])]
    : null;

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <h2 className={styles.title}>Study history</h2>
        <Divider vertical />
        <span className={styles.studyCount}>
          {studyCount} {studyCount === 1 ? 'Study' : 'Studies'}
        </span>
      </div>

      <div className={styles.right}>
        <Dropdown
          menu={{
            items: dateRangeItems,
            onClick: handleDateRangeClick,
            selectedKeys: [dateRange],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            size="small"
            icon={<RiCalendarLine />}
            className={dateRange !== 'all' ? styles.filterActive : undefined}
          >
            {DATE_RANGE_LABELS[dateRange]} <RiArrowDownSLine />
          </Button>
        </Dropdown>

        {dateRange === 'custom' && (
          <DatePicker.RangePicker
            size="small"
            value={rangePickerValue}
            onChange={handleCustomDateChange}
            className={styles.customRangePicker}
          />
        )}

        <Dropdown
          menu={{
            items: studyTypeItems,
            onClick: handleStudyTypeClick,
            selectedKeys: [studyType],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            size="small"
            icon={<RiHeartPulseLine />}
            className={studyType !== 'all' ? styles.filterActive : undefined}
          >
            {STUDY_TYPE_LABELS[studyType]} <RiArrowDownSLine />
          </Button>
        </Dropdown>

        {hasActiveFilters && (
          <button
            type="button"
            className={styles.resetLink}
            onClick={onResetFilters}
          >
            Reset filters
          </button>
        )}

        <Divider vertical />

        <div className={styles.toggleGroup}>
          <Switch
            size="small"
            checked={showReportHistory}
            onChange={onShowReportHistoryChange}
            className={styles.customSwitch}
          />
          <span className={styles.toggleLabel}>Show report history</span>
        </div>
      </div>
    </div>
  );
}
